import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { VISUAL_THEME } from '../assets/visualTheme';
import { formatAchievementLines, getAchievementSummary } from '../data/achievements';
import { getEndingCelebration } from '../data/endingCelebration';
import { GAME_HEIGHT, GAME_WIDTH, SCENE_KEYS, SCORE_TIERS } from '../data/gameConstants';
import { getScoreTier } from '../data/scoreRules';
import { AudioSystem } from '../systems/AudioSystem';
import { FullscreenSystem } from '../systems/FullscreenSystem';
import { SaveSystem } from '../systems/SaveSystem';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';
import { Button } from '../ui/Button';
import { UIStyle } from '../ui/UIStyle';
import { SceneTransition } from '../utils/SceneTransition';

export class EndingScene extends Phaser.Scene {
  private lastCanvasW = 0;
  private lastCanvasH = 0;
  private celebrationPlayed = false;
  private resizeReady = false;

  constructor() {
    super({ key: 'EndingScene' });
  }


  /** Posisi Y baris tombol — HP browser (non-fullscreen) tempel ke bawah layar game yang terlihat. */
  private getEndingButtonRowY(
    btnH: number,
    isMobileBrowser: boolean,
    visibleBottom: number,
    safeBottom: number,
  ): number {
    if (!isMobileBrowser) {
      return safeBottom - btnH / 2 - 8;
    }
    const zoom = ResponsiveSystem.getDisplayScale(this.game);
    const chromePad = Math.max(6, 8 / Math.max(zoom, 0.5));
    return visibleBottom - btnH / 2 - chromePad;
  }

  create(): void {
    SaveSystem.markGameCompleted();

    const save = SaveSystem.load();
    const points = save.rafflesiaPoints;
    const tier = getScoreTier(points);
    const playerName = SaveSystem.getPlayerName();
    const celebration = getEndingCelebration(playerName, tier, points);
    const achievements = getAchievementSummary(save);
    const achievementLines = formatAchievementLines(achievements);

    const forceUltraTight = this.registry.get('endingUltraTight') === true;
    if (forceUltraTight) this.registry.remove('endingUltraTight');

    const isMobileBrowser = ResponsiveSystem.isPhone() && !FullscreenSystem.isActive();
    const visible = ResponsiveSystem.getVisibleGameRect(this.game);

    let safe = ResponsiveSystem.getSafeBounds(this.game, {
      top: isMobileBrowser ? 40 : 48,
      bottom: isMobileBrowser ? 16 : 32,
      left: 16,
      right: 16,
    });
    const isCompact = safe.height < 560 || ResponsiveSystem.isMobile(this);
    const isTight = safe.height < 500;
    const isVeryTight = safe.height < 420;
    const usePhoneLayout = isMobileBrowser || isCompact;

    if (isTight || usePhoneLayout) {
      safe = ResponsiveSystem.getSafeBounds(this.game, {
        top: isVeryTight ? 28 : usePhoneLayout ? 36 : 44,
        bottom: usePhoneLayout ? 12 : 24,
        left: 10,
        right: 10,
      });
    }

    this.cameras.main.setBackgroundColor(0x000000);
    this.drawSceneBackdrop();
    this.spawnConfetti();
    this.drawCharacters(isCompact);
    this.drawRafflesiaFinal(isCompact);

    const headlineColor = points >= SCORE_TIERS.HERO.min
      ? '#ffd166'
      : points >= SCORE_TIERS.VOLUNTEER.min
        ? '#4ade80'
        : '#ffffff';

    const zoom = Math.max(0.42, ResponsiveSystem.getDisplayScale(this.game));
    /** Target ukuran di layar (CSS px). Di HP zoom < 1, font game dinaikkan supaya tetap kebaca. */
    const screenPx = (desiredCss: number, minGame: number): string =>
      `${Math.max(minGame, Math.round(desiredCss / Math.min(zoom, 1)))}px`;

    const headlineSize = screenPx(isCompact ? 20 : 26, isCompact ? 24 : 30);
    const subtitleSize = screenPx(isCompact ? 16 : 19, isCompact ? 18 : 22);

    const btnH = isCompact ? 52 : 58;
    const btnGapFromCard = usePhoneLayout
      ? Math.max(16, isVeryTight ? 12 : 16)
      : isVeryTight ? 16 : isTight ? 18 : isCompact ? 22 : 28;
    const btnRowY = this.getEndingButtonRowY(btnH, usePhoneLayout, visible.bottom, safe.bottom);
    const contentBottom = btnRowY - btnH / 2 - btnGapFromCard;

    const padX = isCompact ? 18 : 32;
    const panelW = Math.min(safe.width - (isCompact ? 4 : 16), isCompact ? 1240 : 1020);
    const panelLeft = safe.centerX - panelW / 2;
    const innerW = panelW - padX * 2;
    const bannerW = Math.min(panelW + 8, safe.width, visible.width - 16);
    const headlineWrapW = Math.max(280, bannerW - 48);

    const measureBlock = (
      content: string,
      style: Phaser.Types.GameObjects.Text.TextStyle,
      wrapW: number,
      lineSpacing: number,
      align: 'center' | 'left' = 'center',
    ): number => {
      const probe = this.add.text(0, 0, content, {
        ...style,
        align,
        lineSpacing,
        wordWrap: { width: wrapW },
      }).setOrigin(align === 'center' ? 0.5 : 0, 0);
      const height = probe.getBounds().height;
      probe.destroy();
      return height;
    };


    const headlineH = measureBlock(
      celebration.headline,
      UIStyle.onBrightBg(headlineSize, headlineColor, true),
      headlineWrapW,
      6,
    );
    const subtitleH = measureBlock(
      celebration.subtitle,
      UIStyle.onBrightBg(subtitleSize, '#e8e8e8', true),
      headlineWrapW,
      4,
    );
    const bannerPadY = isVeryTight ? 8 : isTight ? 10 : isCompact ? 10 : 16;
    const bannerGap = isCompact ? 4 : 8;
    const bannerH = bannerPadY * 2 + headlineH + bannerGap + subtitleH;
    const bannerY = safe.top + bannerH / 2 + 4;

    const bannerBg = this.add.rectangle(
      safe.centerX,
      bannerY,
      bannerW,
      bannerH,
      0x163832,
      0.92,
    )
      .setDepth(25)
      .setStrokeStyle(2, 0xffd166, 0.75)
      .setScale(0.6)
      .setAlpha(0);

    const headline = this.add.text(
      safe.centerX,
      bannerY - bannerH / 2 + bannerPadY,
      celebration.headline,
      {
        ...UIStyle.onBrightBg(headlineSize, headlineColor, true),
        align: 'center',
        wordWrap: { width: headlineWrapW },
      },
    ).setOrigin(0.5, 0).setDepth(26).setScale(0.6).setAlpha(0);

    const subtitle = this.add.text(
      safe.centerX,
      bannerY - bannerH / 2 + bannerPadY + headlineH + bannerGap,
      celebration.subtitle,
      {
        ...UIStyle.onBrightBg(subtitleSize, '#e8e8e8', true),
        align: 'center',
        wordWrap: { width: headlineWrapW },
      },
    ).setOrigin(0.5, 0).setDepth(26).setAlpha(0);

    this.tweens.add({
      targets: [bannerBg, headline],
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 650,
      ease: 'Back.easeOut',
      delay: 200,
    });
    this.tweens.add({
      targets: subtitle,
      alpha: 1,
      duration: 500,
      delay: 550,
    });

    const scoreColor = points >= SCORE_TIERS.HERO.min
      ? '#ffd166'
      : points >= SCORE_TIERS.VOLUNTEER.min
        ? '#4ade80'
        : '#e2e8f0';
    const scoreNumberText = String(points);
    const scoreTierText = `Gelar: ${tier}`;
    const achCol1Lines = achievementLines.slice(0, 3);
    const achCol2Lines = achievementLines.slice(3);

    /** Pisahkan "Label: Nilai" agar label netral & nilai berwarna status — kontras jauh lebih jelas
     * dibanding satu baris penuh berwarna di atas panel gelap. */
    const splitAchievementLine = (line: string): { label: string; value: string } => {
      const idx = line.indexOf(': ');
      if (idx === -1) return { label: line, value: '' };
      return { label: line.slice(0, idx + 1), value: line.slice(idx + 2) };
    };

    const ACH_LABEL_COLOR = '#d7e6f7';
    const achievementValueStyle = (line: string, size: string): Phaser.Types.GameObjects.Text.TextStyle => {
      if (line.includes('✓ Selesai')) {
        return UIStyle.onDarkPanel(size, '#4ade80', true);
      }
      if (line.includes('✗ Belum')) {
        return UIStyle.onDarkPanel(size, '#fb7185', true);
      }
      if (line.includes('Terkena reruntuhan')) {
        return UIStyle.onDarkPanel(size, '#fbbf24', true);
      }
      if (line.startsWith('Keris Pusaka')) {
        return UIStyle.onDarkPanel(size, '#ffd166', true);
      }
      return UIStyle.onDarkPanel(size, '#ffffff', true);
    };

    const achievementLabelStyle = (size: string): Phaser.Types.GameObjects.Text.TextStyle =>
      UIStyle.onDarkPanel(size, ACH_LABEL_COLOR, false);

    /** Jarak horizontal antara label & nilai saat baris tampil inline ("Label: Nilai"). */
    const ACH_VALUE_GAP_X = 10;
    /** Stroke teks Phaser melebar — sisakan ruang supaya tidak tembus garis emas. */
    const STROKE_INSET = 8;
    /** Padding bawah tiap baris — stroke teks Phaser melebar di luar `.height`. */
    const ACH_ROW_BOTTOM_PAD = isCompact ? 6 : 10;

    type AchRowPlan =
      | { mode: 'labelOnly'; label: string; value: string; height: number }
      | { mode: 'inline'; label: string; value: string; labelW: number; height: number }
      | { mode: 'stacked'; label: string; value: string; labelH: number; valueH: number; height: number };

    /** Ukur tinggi teks termasuk stroke — `.height` saja sering terlalu kecil & bikin kotak kepotong. */
    const measureTextVisualWidth = (
      content: string,
      style: Phaser.Types.GameObjects.Text.TextStyle,
    ): number => {
      const probe = this.add.text(0, 0, content, { ...style }).setOrigin(0, 0);
      const w = probe.getBounds().width;
      probe.destroy();
      return w;
    };

    /**
     * Tentukan tata letak satu baris pencapaian — pengukuran pakai getBounds()
     * supaya identik dengan yang dirender (stroke tidak kepotong di bawah kotak).
     */
    const planAchRow = (
      line: string,
      size: string,
      colW: number,
      valueGap: number,
    ): AchRowPlan => {
      const { label, value } = splitAchievementLine(line);
      const wrapW = Math.max(48, colW - STROKE_INSET);
      const labelStyle = achievementLabelStyle(size);
      const labelProbe = this.add.text(0, 0, label, {
        ...labelStyle,
        wordWrap: { width: wrapW },
      }).setOrigin(0, 0);
      const labelBottom = labelProbe.getBounds().bottom;
      const labelW = labelProbe.getBounds().width;

      if (!value) {
        const height = labelBottom + ACH_ROW_BOTTOM_PAD;
        labelProbe.destroy();
        return { mode: 'labelOnly', label, value, height };
      }

      const valueStyle = achievementValueStyle(line, size);
      const valueW = measureTextVisualWidth(value, valueStyle);

      if (labelW + ACH_VALUE_GAP_X + valueW + STROKE_INSET <= wrapW) {
        const valueProbe = this.add.text(labelW + ACH_VALUE_GAP_X, 0, value, {
          ...valueStyle,
        }).setOrigin(0, 0);
        const height = Math.max(labelBottom, valueProbe.getBounds().bottom) + ACH_ROW_BOTTOM_PAD;
        labelProbe.destroy();
        valueProbe.destroy();
        return { mode: 'inline', label, value, labelW, height };
      }

      const valueProbe = this.add.text(0, labelBottom + valueGap, value, {
        ...valueStyle,
        wordWrap: { width: wrapW },
      }).setOrigin(0, 0);
      const valueBottom = valueProbe.getBounds().bottom;
      const labelH = labelBottom;
      const valueH = valueBottom - (labelBottom + valueGap);
      const height = valueBottom + ACH_ROW_BOTTOM_PAD;
      labelProbe.destroy();
      valueProbe.destroy();
      return { mode: 'stacked', label, value, labelH, valueH, height };
    };

    const measureAchColumn = (
      lines: string[],
      size: string,
      colW: number,
      lineSpacing: number,
      valueGap: number,
    ): number => {
      let total = 0;
      lines.forEach((line, index) => {
        total += planAchRow(line, size, colW, valueGap).height;
        if (index < lines.length - 1) total += lineSpacing;
      });
      return total;
    };

    const availableTop = bannerY + bannerH / 2 + (isCompact ? 6 : 12);
    const availableH = contentBottom - availableTop;

    /** Ukuran dasar (skala 1.0, desktop non-compact). Diperkecil proporsional bila konten tak muat. */
    const px = (base: number, scale: number, min = 10) => `${Math.max(min, Math.round(base * scale))}px`;
    const gapPx = (base: number, scale: number, min = 4) => Math.max(min, Math.round(base * scale));

    /**
     * dropLevel menurunkan konten non-esensial saat layar pendek.
     * Font tidak boleh mengecil sampai tidak terbaca — lebih baik drop narasi
     * daripada mengecilkan skor & pencapaian.
     * 0 = tampil semua, 1 = sembunyikan divider, 2 = + sembunyikan kutipan,
     * 3 = + sembunyikan flavor, 4 = + sembunyikan narasi,
     * 5 = + sembunyikan judul "Rafflesia Final".
     */
    const buildLayout = (scale: number, dropLevel: number) => {
      const showDivider = dropLevel < 1;
      const showQuote = dropLevel < 2;
      const showStory = dropLevel < 4;
      const showFlavor = dropLevel < 3 && celebration.tierFlavor.trim().length > 0;
      const showTitle = dropLevel < 5;
      const boost = (base: number, minCss: number) =>
        Math.max(base, Math.round(minCss / Math.min(zoom, 1)));
      const gap = gapPx(isCompact ? 12 : 18, scale, 10);
      const scoreAchGap = gapPx(isCompact ? 12 : 20, scale, 10);
      const nestedInsetX = gapPx(12, scale, 8);
      const nestedBoxW = innerW - nestedInsetX * 2;
      const achGap = gapPx(28, scale, 20);
      const achLine = gapPx(isCompact ? 6 : 12, scale, 4);
      const achValueGap = gapPx(3, scale, 2);
      const titleStyle = UIStyle.panelTitle(px(boost(30, 20), scale, 20));
      const bodyStyle = UIStyle.panelBody(px(boost(19, 16), scale, 16));
      const quoteStyle = UIStyle.onDarkPanel(px(boost(18, 15), scale, 15), '#e8eef4', false);
      const flavorStyle = UIStyle.onDarkPanel(px(boost(18, 15), scale, 15), '#7dffb8', true);
      const achTitleStyle = UIStyle.onDarkPanel(px(boost(24, 18), scale, 18), '#ffd166', true);
      const achFontSize = px(boost(22, 17), scale, 17);
      const scoreCaptionStyle = UIStyle.onDarkPanel(px(boost(19, 15), scale, 15), '#d7e6f7', false);
      const scoreNumberStyle = UIStyle.onDarkPanel(
        px(boost(isCompact ? 40 : 52, 28), scale, 28),
        scoreColor,
        true,
      );
      const scoreTierStyle = UIStyle.onDarkPanel(px(boost(22, 16), scale, 16), scoreColor, true);
      const scoreInnerGap = gapPx(isCompact ? 4 : 6, scale, 3);
      const scorePadY = gapPx(isCompact ? 10 : 16, scale, 8);
      const scorePadX = gapPx(16, scale, 12);
      const scoreStrokeExtra = 6;
      const bodyLineSpacing = gapPx(7, scale, 4);

      const titleH = showTitle ? measureBlock('Rafflesia Final', titleStyle, innerW, 4) : 0;
      const storyH = showStory
        ? measureBlock(celebration.storyLine, bodyStyle, innerW, bodyLineSpacing)
        : 0;
      const quoteH = showQuote ? measureBlock(celebration.rafflesiaFinal, quoteStyle, innerW, bodyLineSpacing) : 0;
      const flavorH = showFlavor ? measureBlock(celebration.tierFlavor, flavorStyle, innerW, bodyLineSpacing) : 0;
      const scoreCaptionH = measureBlock('Rafflesia Point', scoreCaptionStyle, nestedBoxW - scorePadX * 2, 0);
      const scoreNumberH = measureBlock(scoreNumberText, scoreNumberStyle, nestedBoxW - scorePadX * 2, 0);
      const scoreTierH = measureBlock(scoreTierText, scoreTierStyle, nestedBoxW - scorePadX * 2, 0);
      const scoreH = scoreCaptionH + scoreInnerGap + scoreNumberH + scoreInnerGap + scoreTierH;
      const achBoxPadTop = gapPx(isCompact ? 10 : 16, scale, 8);
      const achBoxPadBottom = gapPx(isCompact ? 8 : 20, scale, 8);
      const achBoxPadX = gapPx(isCompact ? 18 : 20, scale, 14);
      const achTitleGap = gapPx(isCompact ? 8 : 12, scale, 6);
      const achTitleH = measureBlock('Pencapaian', achTitleStyle, nestedBoxW - achBoxPadX * 2, 4, 'left');
      const achColW = (nestedBoxW - achBoxPadX * 2 - achGap) / 2;
      const achCol1H = measureAchColumn(achCol1Lines, achFontSize, achColW, achLine, achValueGap);
      const achCol2H = measureAchColumn(achCol2Lines, achFontSize, achColW, achLine, achValueGap);
      const achRowsH = Math.max(achCol1H, achCol2H);
      const achBoxH = achBoxPadTop + achTitleH + achTitleGap + achRowsH + achBoxPadBottom;
      const scoreBoxH = scoreH + scorePadY * 2 + scoreStrokeExtra;
      const contentH = (showTitle ? titleH + gap : 0)
        + (showStory ? storyH + gap : 0)
        + (showQuote ? quoteH + gap : 0)
        + (showDivider ? 1 + gap : 0)
        + (showFlavor ? flavorH + gap : 0)
        + scoreBoxH + scoreAchGap + achBoxH;

      return {
        gap,
        scoreAchGap,
        nestedInsetX,
        nestedBoxW,
        achGap,
        achLine,
        titleStyle,
        bodyStyle,
        quoteStyle,
        flavorStyle,
        achTitleStyle,
        achFontSize,
        scoreCaptionStyle,
        scoreNumberStyle,
        scoreTierStyle,
        scoreInnerGap,
        scorePadY,
        scorePadX,
        scoreStrokeExtra,
        bodyLineSpacing,
        showDivider,
        showQuote,
        showStory,
        showFlavor,
        showTitle,
        titleH,
        storyH,
        quoteH,
        flavorH,
        scoreH,
        scoreCaptionH,
        scoreNumberH,
        scoreTierH,
        achTitleH,
        achColW,
        achRowsH,
        achTitleGap,
        achValueGap,
        achBoxPadX,
        achBoxPadTop,
        achBoxPadBottom,
        achBoxH,
        scoreBoxH,
        contentH,
      };
    };

    const cardPadTopFor = (scale: number) => gapPx(isCompact ? 10 : 16, scale, 8);
    const cardPadBottomFor = (scale: number) => gapPx(isCompact ? 12 : 18, scale, 10);
    const layoutSlack = 12;

    let layout = buildLayout(1, 0);
    let cardPadTop = cardPadTopFor(1);
    let cardPadBottom = cardPadBottomFor(1);
    const scaleSteps = forceUltraTight ? [1, 0.96] : [1, 0.96];
    let fitFound = false;
    const startDropLevel = forceUltraTight || usePhoneLayout
      ? 5
      : isVeryTight ? 3 : isTight ? 2 : isCompact ? 1 : 0;
    for (let dropLevel = startDropLevel; dropLevel <= 5 && !fitFound; dropLevel++) {
      for (const s of scaleSteps) {
        const candidate = buildLayout(s, dropLevel);
        cardPadTop = cardPadTopFor(s);
        cardPadBottom = cardPadBottomFor(s);
        layout = candidate;
        if (candidate.contentH + cardPadTop + cardPadBottom + layoutSlack <= availableH) {
          fitFound = true;
          break;
        }
      }
    }

    // Tombol SELALU pada posisi tetap di safe area — kartu tidak pernah mendorongnya keluar layar.
    let cardH = Math.min(layout.contentH + cardPadTop + cardPadBottom, availableH);
    const cardTop = availableTop;
    let finalBtnRowY = btnRowY;

    const card = UIStyle.createContentCard(this, panelLeft, cardTop, panelW, cardH);
    card.setDepth(20).setAlpha(0).setScale(0.94);

    const addDivider = (y: number): Phaser.GameObjects.Graphics => {
      const line = this.add.graphics().setDepth(21).setAlpha(0);
      line.lineStyle(1, 0xe7c487, 0.28);
      line.beginPath();
      line.moveTo(panelLeft + padX, y);
      line.lineTo(panelLeft + panelW - padX, y);
      line.strokePath();
      return line;
    };

    let cursorY = cardTop + cardPadTop;
    const textX = safe.centerX;
    const {
      gap,
      scoreAchGap,
      nestedInsetX,
      nestedBoxW,
      achGap,
      achLine,
      titleStyle,
      bodyStyle,
      quoteStyle,
      flavorStyle,
      achTitleStyle,
      achFontSize,
      scoreCaptionStyle,
      scoreNumberStyle,
      scoreTierStyle,
      scoreInnerGap,
      scorePadX,
      scorePadY,
      bodyLineSpacing,
      showDivider,
      showQuote,
      showStory,
      showFlavor,
      showTitle,
      titleH,
      storyH,
      quoteH,
      flavorH,
      achTitleH,
      achTitleGap,
      achValueGap,
      achBoxPadX,
      achBoxPadTop,
      achBoxPadBottom,
      scoreBoxH,
      scoreCaptionH,
      scoreNumberH,
    } = layout;

    const maskRounded = (
      x: number,
      y: number,
      w: number,
      h: number,
      radius: number,
    ): Phaser.Display.Masks.GeometryMask => {
      const g = this.add.graphics().setVisible(false);
      g.fillStyle(0xffffff, 1);
      g.fillRoundedRect(x + 3, y + 3, Math.max(8, w - 6), Math.max(8, h - 6), Math.max(2, radius - 2));
      return g.createGeometryMask();
    };

    const drawNestedBox = (
      x: number,
      y: number,
      w: number,
      h: number,
      fill: number,
      fillAlpha: number,
      stroke: number,
      strokeAlpha: number,
    ): Phaser.GameObjects.Graphics => {
      const g = this.add.graphics().setDepth(22).setAlpha(0);
      const r = 12;
      g.fillStyle(fill, fillAlpha);
      g.fillRoundedRect(x, y, w, h, r);
      g.lineStyle(2, stroke, strokeAlpha);
      g.strokeRoundedRect(x + 1, y + 1, w - 2, h - 2, r - 1);
      return g;
    };

    const panelTitle = showTitle
      ? this.add.text(textX, cursorY, 'Rafflesia Final', {
        ...titleStyle,
        align: 'center',
      }).setOrigin(0.5, 0).setDepth(21).setAlpha(0)
      : undefined;
    if (showTitle) cursorY += titleH + gap;

    const storyText = showStory
      ? this.add.text(textX, cursorY, celebration.storyLine, {
        ...bodyStyle,
        align: 'center',
        wordWrap: { width: innerW },
        lineSpacing: bodyLineSpacing,
      }).setOrigin(0.5, 0).setDepth(21).setAlpha(0)
      : undefined;
    if (showStory) cursorY += storyH + gap;

    let rafflesiaFinalText: Phaser.GameObjects.Text | undefined;
    if (showQuote) {
      rafflesiaFinalText = this.add.text(textX, cursorY, celebration.rafflesiaFinal, {
        ...quoteStyle,
        align: 'center',
        wordWrap: { width: innerW },
        lineSpacing: bodyLineSpacing,
      }).setOrigin(0.5, 0).setDepth(21).setAlpha(0);
      cursorY += quoteH + gap;
    }

    let divider1: Phaser.GameObjects.Graphics | undefined;
    if (showDivider) {
      divider1 = addDivider(cursorY);
      cursorY += 1 + gap;
    }

    let flavorText: Phaser.GameObjects.Text | undefined;
    if (showFlavor) {
      flavorText = this.add.text(textX, cursorY, celebration.tierFlavor, {
        ...flavorStyle,
        align: 'center',
        wordWrap: { width: innerW },
        lineSpacing: bodyLineSpacing,
      }).setOrigin(0.5, 0).setDepth(21).setAlpha(0);
      cursorY += flavorH + gap;
    }

    const scoreBoxTop = cursorY;
    const scoreBoxW = nestedBoxW;
    const scoreBoxLeft = panelLeft + padX + nestedInsetX;
    const scoreBox = drawNestedBox(
      scoreBoxLeft,
      scoreBoxTop,
      scoreBoxW,
      scoreBoxH,
      0x1a3048,
      0.88,
      0xe7c487,
      0.5,
    );

    const scoreCenterX = scoreBoxLeft + scoreBoxW / 2;
    const scoreWrapW = scoreBoxW - scorePadX * 2;
    let scoreCursorY = scoreBoxTop + scorePadY;
    const scoreCaption = this.add.text(scoreCenterX, scoreCursorY, 'Rafflesia Point', {
      ...scoreCaptionStyle,
      align: 'center',
      wordWrap: { width: scoreWrapW },
    }).setOrigin(0.5, 0).setDepth(23).setAlpha(0);
    scoreCursorY += scoreCaptionH + scoreInnerGap;
    const scoreNumber = this.add.text(scoreCenterX, scoreCursorY, scoreNumberText, {
      ...scoreNumberStyle,
      align: 'center',
      wordWrap: { width: scoreWrapW },
    }).setOrigin(0.5, 0).setDepth(23).setAlpha(0);
    scoreCursorY += scoreNumberH + scoreInnerGap;
    const scoreTierLabel = this.add.text(scoreCenterX, scoreCursorY, scoreTierText, {
      ...scoreTierStyle,
      align: 'center',
      wordWrap: { width: scoreWrapW },
    }).setOrigin(0.5, 0).setDepth(23).setAlpha(0);
    cursorY += scoreBoxH + scoreAchGap;

    const repaintContentCard = (
      g: Phaser.GameObjects.Graphics,
      x: number,
      y: number,
      w: number,
      h: number,
    ): void => {
      g.clear();
      g.fillStyle(0x10202d, 0.84);
      g.fillRoundedRect(x, y, w, h, 20);
      g.fillStyle(0x29425d, 0.28);
      g.fillRoundedRect(x + 6, y + 6, w - 12, Math.max(0, h * 0.2), 14);
      g.fillStyle(0xffffff, 0.03);
      g.fillRoundedRect(x + 8, y + 8, w - 16, h - 16, 14);
      g.lineStyle(2, 0xe7c487, 0.42);
      g.strokeRoundedRect(x, y, w, h, 20);
    };

    const achBoxTop = cursorY;
    const achBoxW = nestedBoxW;
    const achBoxLeft = panelLeft + padX + nestedInsetX;

    const achInnerLeft = achBoxLeft + achBoxPadX;
    const achContentW = achBoxW - achBoxPadX * 2;
    const achTextW = (achContentW - achGap) / 2;

    const achTitle = this.add.text(achInnerLeft, achBoxTop + achBoxPadTop, 'Pencapaian', {
      ...achTitleStyle,
      wordWrap: { width: achContentW },
    }).setOrigin(0, 0).setDepth(23).setAlpha(0);

    const achTextY = achBoxTop + achBoxPadTop + achTitleH + achTitleGap;
    const achColObjects: Phaser.GameObjects.Text[] = [];

    const addAchColumn = (x: number, lines: string[]) => {
      let y = achTextY;
      const wrapW = Math.max(48, achTextW - STROKE_INSET);
      lines.forEach((line, index) => {
        const plan = planAchRow(line, achFontSize, achTextW, achValueGap);
        const labelText = this.add.text(x, y, plan.label, {
          ...achievementLabelStyle(achFontSize),
          wordWrap: { width: wrapW },
        }).setOrigin(0, 0).setDepth(23).setAlpha(0);
        achColObjects.push(labelText);

        if (plan.mode === 'inline') {
          const remain = Math.max(48, wrapW - plan.labelW - ACH_VALUE_GAP_X);
          const valueText = this.add.text(x + plan.labelW + ACH_VALUE_GAP_X, y, plan.value, {
            ...achievementValueStyle(line, achFontSize),
            wordWrap: { width: remain },
          }).setOrigin(0, 0).setDepth(23).setAlpha(0);
          achColObjects.push(valueText);
        } else if (plan.mode === 'stacked') {
          const valueText = this.add.text(x, y + plan.labelH + achValueGap, plan.value, {
            ...achievementValueStyle(line, achFontSize),
            wordWrap: { width: wrapW },
          }).setOrigin(0, 0).setDepth(23).setAlpha(0);
          achColObjects.push(valueText);
        }

        y += plan.height;
        if (index < lines.length - 1) y += achLine;
      });
    };

    addAchColumn(achInnerLeft, achCol1Lines);
    addAchColumn(achInnerLeft + achTextW + achGap, achCol2Lines);

    const achContentBottom = Math.max(
      achTitle.getBounds().bottom,
      ...achColObjects.map((obj) => obj.getBounds().bottom),
    );
    const maxAchBoxH = Math.max(
      achTitleH + achBoxPadTop + achBoxPadBottom + 24,
      (cardTop + availableH) - achBoxTop - cardPadBottom,
    );
    const finalAchBoxH = Math.min(
      achContentBottom - achBoxTop + achBoxPadBottom,
      maxAchBoxH,
    );

    const achBox = drawNestedBox(
      achBoxLeft,
      achBoxTop,
      achBoxW,
      finalAchBoxH,
      0x1a2838,
      0.94,
      0xffd166,
      0.48,
    );

    cursorY = achBoxTop + finalAchBoxH;

    const neededCardH = cursorY - cardTop + cardPadBottom;
    const finalCardH = Math.min(Math.max(cardH, neededCardH), availableH);
    if (Math.abs(finalCardH - cardH) > 0.5) {
      repaintContentCard(card, panelLeft, cardTop, panelW, finalCardH);
      cardH = finalCardH;
    }

    const panelContent = [
      panelTitle,
      storyText,
      rafflesiaFinalText,
      divider1,
      flavorText,
      scoreBox,
      scoreCaption,
      scoreNumber,
      scoreTierLabel,
      achBox,
      achTitle,
      ...achColObjects,
    ].filter((obj) => obj !== undefined) as Phaser.GameObjects.GameObject[];

    this.add.container(0, 0, panelContent).setDepth(21);

    const bannerLeft = safe.centerX - bannerW / 2;
    const bannerTop = bannerY - bannerH / 2;
    const bannerMask = maskRounded(bannerLeft, bannerTop, bannerW, bannerH, 10);
    headline.setMask(bannerMask);
    subtitle.setMask(bannerMask);

    const scoreMask = maskRounded(scoreBoxLeft, scoreBoxTop, scoreBoxW, scoreBoxH, 12);
    scoreCaption.setMask(scoreMask);
    scoreNumber.setMask(scoreMask);
    scoreTierLabel.setMask(scoreMask);

    const achMask = maskRounded(achBoxLeft, achBoxTop, achBoxW, finalAchBoxH, 12);
    achTitle.setMask(achMask);
    achColObjects.forEach((obj) => obj.setMask(achMask));

    const cardMask = maskRounded(panelLeft, cardTop, panelW, cardH, 20);
    [panelTitle, storyText, rafflesiaFinalText, flavorText].forEach((obj) => {
      obj?.setMask(cardMask);
    });

    const canvasRect = this.game.canvas.getBoundingClientRect();
    this.lastCanvasW = canvasRect.width;
    this.lastCanvasH = canvasRect.height;
    this.resizeReady = false;
    this.time.delayedCall(400, () => {
      this.resizeReady = true;
    });

    this.scale.on(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    this.tweens.add({
      targets: card,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 700,
      ease: 'Back.easeOut',
      delay: 900,
    });

    panelContent.forEach((obj, i) => {
      this.tweens.add({
        targets: obj,
        alpha: 1,
        duration: 450,
        delay: 980 + i * 55,
      });
    });

    this.time.delayedCall(1500, () => this.buildButtons({
      rowY: finalBtnRowY,
      btnH,
      safe,
      isCompact,
      fontSize: screenPx(isCompact ? 16 : 17, isCompact ? 20 : 22),
    }));
    this.playCelebrationSequence(celebration.speech);
    this.cameras.main.fadeIn(800, 0, 0, 0);
  }

  private handleResize = (): void => {
    if (!this.resizeReady || !this.scene.isActive('EndingScene')) return;

    const canvasRect = this.game.canvas.getBoundingClientRect();
    const nextW = canvasRect.width;
    const nextH = canvasRect.height;
    const dw = Math.abs(nextW - this.lastCanvasW);
    const dh = Math.abs(nextH - this.lastCanvasH);

    if (dw < 40 && dh < 100) return;

    this.lastCanvasW = nextW;
    this.lastCanvasH = nextH;
    if (this.celebrationPlayed) {
      this.registry.set('endingSkipSpeech', true);
    }
    this.time.delayedCall(80, () => {
      if (this.scene.isActive('EndingScene')) {
        this.scene.restart();
      }
    });
  };

  shutdown(): void {
    this.scale.off(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    AudioSystem.stopSpeech();
  }

  private playCelebrationSequence(speech: string): void {
    if (this.registry.get('endingSkipSpeech') === true) {
      this.registry.remove('endingSkipSpeech');
      this.celebrationPlayed = true;
      return;
    }
    this.celebrationPlayed = true;
    this.time.delayedCall(350, () => AudioSystem.playVictoryFanfare());
    this.time.delayedCall(1100, () => AudioSystem.playCelebrationChime());
    this.time.delayedCall(1400, () => AudioSystem.speakCongratulations(speech));
  }

  private spawnConfetti(): void {
    const colors = [0xffd166, 0x52b788, 0xff4757, 0x5dd5ff, 0xff8a65, 0xffe066];

    for (let i = 0; i < 36; i++) {
      const w = Phaser.Math.Between(5, 10);
      const h = Phaser.Math.Between(8, 14);
      const piece = this.add.rectangle(
        Phaser.Math.Between(60, GAME_WIDTH - 60),
        Phaser.Math.Between(-120, -20),
        w,
        h,
        Phaser.Utils.Array.GetRandom(colors),
        0.85,
      ).setDepth(14).setAngle(Phaser.Math.Between(0, 360));

      this.tweens.add({
        targets: piece,
        y: GAME_HEIGHT + 60,
        x: piece.x + Phaser.Math.Between(-100, 100),
        angle: piece.angle + Phaser.Math.Between(360, 720),
        duration: Phaser.Math.Between(2800, 4800),
        delay: Phaser.Math.Between(0, 1500),
        ease: 'Cubic.easeIn',
        repeat: -1,
        onRepeat: () => {
          piece.x = Phaser.Math.Between(60, GAME_WIDTH - 60);
          piece.y = Phaser.Math.Between(-120, -20);
        },
      });
    }
  }

  private drawSceneBackdrop(): void {
    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, TEXTURE_KEYS.BG_ENDING)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
      .setDepth(0);

    this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, GAME_WIDTH, GAME_HEIGHT,
      VISUAL_THEME.zoneWarmth.ending, 0.1,
    ).setDepth(0.5);

    this.add.image(GAME_WIDTH / 2, 248, TEXTURE_KEYS.SAFE_GATHERING)
      .setScale(0.72)
      .setAlpha(0.45)
      .setDepth(1);

    const sky = this.add.graphics().setDepth(1);
    sky.fillStyle(0x9ee5ff, 0.35);
    sky.fillRect(0, 0, GAME_WIDTH, 180);

    const glow = this.add.circle(GAME_WIDTH / 2, 210, 140, 0x86efac, 0.16);
    glow.setDepth(1);
    this.tweens.add({
      targets: glow,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.08,
      duration: 1800,
      yoyo: true,
      repeat: -1,
    });
  }

  private drawCharacters(isCompact: boolean): void {
    const wargaY = isCompact ? 318 : 328;
    [320, 420, 880, 980].forEach((x, i) => {
      const w = this.add.image(x, wargaY + (i % 2) * 8, TEXTURE_KEYS.WARGA)
        .setScale(0.85)
        .setAlpha(0)
        .setDepth(5);
      this.tweens.add({
        targets: w,
        alpha: 0.42,
        y: w.y - 6,
        duration: 500,
        delay: 600 + i * 100,
        ease: 'Back.easeOut',
      });
    });

    const bujang = this.add.image(GAME_WIDTH / 2 - 70, wargaY - 18, TEXTURE_KEYS.PLAYER_IDLE_0)
      .setScale(0.72)
      .setAlpha(0)
      .setDepth(10);
    const ibundo = this.add.image(GAME_WIDTH / 2 + 70, wargaY - 20, TEXTURE_KEYS.IBUNDO)
      .setScale(0.78)
      .setAlpha(0)
      .setDepth(10);

    this.tweens.add({
      targets: [bujang, ibundo],
      alpha: 1,
      scaleX: 1.05,
      scaleY: 1.05,
      y: '-=6',
      duration: 700,
      delay: 500,
      ease: 'Back.easeOut',
    });
  }

  private drawRafflesiaFinal(isCompact: boolean): void {
    const x = isCompact ? 108 : 120;
    const y = isCompact ? 248 : 258;
    const raff = this.add.image(x, y, TEXTURE_KEYS.RAFFLESIA)
      .setScale(isCompact ? 0.32 : 0.36)
      .setAlpha(0)
      .setDepth(4);

    const glow = this.add.circle(x, y, isCompact ? 26 : 30, 0xffd166, 0).setDepth(3);

    this.tweens.add({
      targets: [raff, glow],
      alpha: 1,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 800,
      delay: 700,
      ease: 'Back.easeOut',
      onComplete: () => {
        glow.setFillStyle(0xffd166, 0.22);
      },
    });
  }

  private buildButtons(opts: {
    rowY: number;
    btnH: number;
    safe: ReturnType<typeof ResponsiveSystem.getSafeBounds>;
    isCompact: boolean;
    fontSize: string;
  }): void {
    const { rowY, btnH, safe, isCompact, fontSize } = opts;

    const btnW = isCompact ? 228 : 252;
    const btnGap = isCompact ? 20 : 28;
    const totalBtnW = btnW * 2 + btnGap;
    const startX = safe.centerX - totalBtnW / 2 + btnW / 2;

    const fadeInBtn = (targets: Phaser.GameObjects.GameObject[], delay: number) => {
      this.tweens.add({
        targets,
        alpha: 1,
        duration: 420,
        delay,
        ease: 'Cubic.easeOut',
      });
    };

    const btn1 = new Button(this, {
      x: startX,
      y: rowY,
      text: 'Main Lagi',
      width: btnW,
      height: btnH,
      fontSize,
      depth: 35,
      onClick: () => {
        AudioSystem.stopSpeech();
        SaveSystem.clear();
        SceneTransition.startNextFrame(this, SCENE_KEYS.NAME_INPUT);
      },
    });

    const btn2 = new Button(this, {
      x: startX + btnW + btnGap,
      y: rowY,
      text: 'Kembali ke Menu',
      width: btnW,
      height: btnH,
      fontSize,
      depth: 35,
      onClick: () => {
        AudioSystem.stopSpeech();
        SceneTransition.startNextFrame(this, SCENE_KEYS.MAIN_MENU);
      },
    });

    btn1.setAlpha(0);
    btn2.setAlpha(0);
    fadeInBtn([btn1.getContainer()], 0);
    fadeInBtn([btn2.getContainer()], 60);
  }
}
